import { clsx } from 'clsx';
import { FocusEvent, ReactNode, useEffect, useRef } from 'react';
import { canDetectAuthViewport, isAuthDesktopViewport } from '../viewport';
import * as styles from './FlowShell.css';

type FlowShellProps = {
  mainContent: ReactNode;
  sideContent?: ReactNode;
  layoutMode?: 'single-column' | 'two-column';
  showRightDots?: boolean;
};

const KEYBOARD_SCROLL_DELAY_MS = 260;
const AUTH_FORM_BOX_SELECTOR = '[data-auth-form-box]';

const shouldScrollFocusedInputIntoView = (): boolean => {
  if (!canDetectAuthViewport()) {
    return false;
  }

  return !isAuthDesktopViewport();
};

export const FlowShell = ({
  mainContent,
  sideContent,
  layoutMode = 'single-column',
  showRightDots = false,
}: FlowShellProps) => {
  const shellRef = useRef<HTMLElement | null>(null);
  const mainPanelRef = useRef<HTMLElement | null>(null);
  const delayedScrollRef = useRef<number | null>(null);
  const hasTwoColumns = layoutMode === 'two-column' && Boolean(sideContent);
  const hasRightDots = showRightDots || hasTwoColumns;

  useEffect(() => {
    const shellElement = shellRef.current;
    const mainPanelElement = mainPanelRef.current;
    if (!shellElement) {
      return;
    }

    if (!hasRightDots) {
      shellElement.style.removeProperty('--auth-dots-start');
      return;
    }

    if (!hasTwoColumns) {
      shellElement.style.setProperty('--auth-dots-start', '0px');
      return () => {
        shellElement.style.removeProperty('--auth-dots-start');
      };
    }

    if (!mainPanelElement) {
      shellElement.style.removeProperty('--auth-dots-start');
      return;
    }

    const formBoxElement = mainPanelElement.querySelector<HTMLElement>(
      AUTH_FORM_BOX_SELECTOR,
    );
    const measuredElement = formBoxElement ?? mainPanelElement;

    const updateDotsStart = () => {
      const shellRect = shellElement.getBoundingClientRect();
      const measuredElementRect = measuredElement.getBoundingClientRect();
      const dotsStart = Math.max(
        0,
        Math.round(measuredElementRect.right - shellRect.left),
      );
      shellElement.style.setProperty('--auth-dots-start', `${dotsStart}px`);
    };

    updateDotsStart();

    const resizeObserver =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(updateDotsStart)
        : null;
    resizeObserver?.observe(shellElement);
    resizeObserver?.observe(mainPanelElement);
    if (measuredElement !== mainPanelElement) {
      resizeObserver?.observe(measuredElement);
    }
    window.addEventListener('resize', updateDotsStart);
    window.addEventListener('orientationchange', updateDotsStart);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateDotsStart);
      window.removeEventListener('orientationchange', updateDotsStart);
      shellElement.style.removeProperty('--auth-dots-start');
    };
  }, [hasRightDots, hasTwoColumns]);

  useEffect(() => {
    if (!canDetectAuthViewport()) {
      return;
    }

    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }

    const updateKeyboardInset = (): void => {
      if (isAuthDesktopViewport()) {
        document.documentElement.style.setProperty(
          '--auth-keyboard-inset',
          '0px',
        );
        return;
      }

      const keyboardInset = Math.max(
        0,
        window.innerHeight - (viewport.height + viewport.offsetTop),
      );

      document.documentElement.style.setProperty(
        '--auth-keyboard-inset',
        `${Math.round(keyboardInset)}px`,
      );
    };

    updateKeyboardInset();
    viewport.addEventListener('resize', updateKeyboardInset);
    viewport.addEventListener('scroll', updateKeyboardInset);
    window.addEventListener('resize', updateKeyboardInset);
    window.addEventListener('orientationchange', updateKeyboardInset);

    return () => {
      viewport.removeEventListener('resize', updateKeyboardInset);
      viewport.removeEventListener('scroll', updateKeyboardInset);
      window.removeEventListener('resize', updateKeyboardInset);
      window.removeEventListener('orientationchange', updateKeyboardInset);
      document.documentElement.style.setProperty(
        '--auth-keyboard-inset',
        '0px',
      );
    };
  }, []);

  useEffect(() => {
    return () => {
      if (delayedScrollRef.current !== null) {
        window.clearTimeout(delayedScrollRef.current);
      }
    };
  }, []);

  const handleFocusCapture = (event: FocusEvent<HTMLElement>): void => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const isInputLikeElement =
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable;

    if (!isInputLikeElement) {
      return;
    }

    if (!shouldScrollFocusedInputIntoView()) {
      return;
    }

    if (delayedScrollRef.current !== null) {
      window.clearTimeout(delayedScrollRef.current);
      delayedScrollRef.current = null;
    }

    const scrollTargetToCenter = (): void => {
      target.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    };

    scrollTargetToCenter();

    // Keyboard opening can shift viewport after focus; run a second scroll pass.
    delayedScrollRef.current = window.setTimeout(() => {
      if (!target.isConnected) {
        delayedScrollRef.current = null;
        return;
      }

      scrollTargetToCenter();
      delayedScrollRef.current = null;
    }, KEYBOARD_SCROLL_DELAY_MS);
  };

  return (
    <main
      ref={shellRef}
      className={clsx(styles.shell, hasRightDots && styles.shellWithRightDots)}
      onFocusCapture={handleFocusCapture}
    >
      <div
        className={clsx(
          styles.shellLayoutBase,
          hasTwoColumns
            ? styles.shellLayoutTwoColumns
            : styles.shellLayoutSingleColumn,
        )}
      >
        <section
          ref={mainPanelRef}
          className={styles.mainPanel}
          data-testid="registration-main-panel"
        >
          {mainContent}
        </section>
        {sideContent ? (
          <aside
            className={styles.sidePanel}
            data-testid="registration-side-panel"
          >
            {sideContent}
          </aside>
        ) : null}
      </div>
    </main>
  );
};
